import { AmountsCell, FeeText, NegativeAmount, PositiveAmount, RateText, StatusBadge, TransactionLogoFirst, TransactionLogoSecond, TransactionLogoStack, TransactionTypeCell } from './style'
import React from 'react'
import { ChartPoint } from '../../components/LineChart'
import { getMethod } from '../../util/Managed'
import Radom, { ISwapCandle, ISwapMarketTicker, ManagedBalance, Swap, SwapStatus, Transaction } from '../../state/Radom'
import TimeText, { TimeFormatType } from '../../components/TimeText'
import { roundAssetAmount } from '../../util/Util'
import { RADOM_COLORS } from '../../util/Constants'
import { TIER_1_PAIRS, TIER_2_PAIRS } from '../FeesSettings'

export const buildTransactionRow = (tx: Transaction, idx: number): JSX.Element[] => {
  const payload = tx.payload as Swap
  const fromMethod = getMethod(payload.fromNetwork, payload.fromToken)
  const toMethod = getMethod(payload.toNetwork, payload.toToken)
  const exchangeRate = (+(payload?.toAmount ?? 0)) / (+payload?.fromAmount || 1)

  return [
    <TransactionTypeCell key={`type-${idx}`}>
      <TransactionLogoStack>
        <TransactionLogoFirst src={fromMethod.logo} alt={fromMethod.ticker} />
        <TransactionLogoSecond src={toMethod.logo} alt={toMethod.ticker} />
      </TransactionLogoStack>
    </TransactionTypeCell>,

    <StatusBadge key={`status-${idx}`} $status={payload.status}>
      {STATUS_LABELS[payload.status]}
    </StatusBadge>,

    <RateText key={`rate-${idx}`}> 1 {fromMethod.ticker} = {roundAssetAmount(toMethod.ticker, exchangeRate)} {toMethod.ticker}</RateText>,

    <AmountsCell key={`amounts-${idx}`}>
      <NegativeAmount>−{roundAssetAmount(fromMethod.ticker, payload.fromAmount)} {fromMethod.ticker}</NegativeAmount>
      <PositiveAmount>+{roundAssetAmount(toMethod.ticker, payload.toAmount ?? 0)} {toMethod.ticker}</PositiveAmount>
    </AmountsCell>,

    <FeeText key={`fee-${idx}`}>{payload.swapFee ? `${roundAssetAmount(fromMethod.ticker, payload.swapFee)} ${fromMethod.ticker}` : '—'}</FeeText>,
    <TimeText key={`date-${payload.externalId}`} date={new Date(payload.createdAt)}
      formatModes={[TimeFormatType.ShortUTCJustDay]} />
  ]
}
export const balanceForAsset = (
  balances: ManagedBalance[],
  asset: SwapAssetRef,
  ticker: string
): number => balances
  .filter(b => !b.treasuryId)
  .filter(b => {
    if (b.asset) {
      return b.asset.toLowerCase() === ticker.toLowerCase()
    }
    if (!b.network) {
      return false
    }
    return b.network.toLowerCase() === asset.network.toLowerCase() &&
      (b.token?.toLowerCase() ?? undefined) === (asset.token?.toLowerCase() ?? undefined)
  })
  .reduce((sum, b) => sum + Number(b.balance), 0)

export const isSwapForPair = (tx: Transaction, tickerA: string, tickerB: string): boolean => {
  const payload = tx.payload as Swap
  const from = getMethod(payload.fromNetwork, payload.fromToken).ticker
  const to = getMethod(payload.toNetwork, payload.toToken).ticker
  return (from === tickerA && to === tickerB) || (from === tickerB && to === tickerA)
}

export type TransactionStatus = 'pending' | 'completed' | 'failed'

export const STATUS_LABELS: Record<SwapStatus, string> = {
  Failed: 'Failed',
  Pending: 'Pending',
  Finalized: 'Completed',
  Initiated: 'Initiated'
}

export interface MockTransaction {
  status: TransactionStatus
  rate: string
  fromAmount: string
  toAmount: string
  fee: string
  date: string
}

export const parseCandle = (raw: ISwapCandle): ChartPoint => ({
  x: raw.ts,
  y: raw.close
})

export type ChartRangeTab = '1D' | '1W' | '1M' | '3M' | '1Y'

interface CandleConfig {
  bar: string
  limit: number
}

export const CHART_RANGE_CONFIG: Record<ChartRangeTab, CandleConfig> = {
  '1D': { bar: '5m', limit: 288 },
  '1W': { bar: '1H', limit: 168 },
  '1M': { bar: '4H', limit: 180 },
  '3M': { bar: '1D', limit: 90 },
  '1Y': { bar: '1W', limit: 52 }
}

const TICKER_PRIORITY = [
  'BTC', 'ETH', 'SOL', 'BNB', 'XMR', 'AVAX', 'MATIC', 'POL', 'DOT', 'TRX', 'LTC', 'BCH', 'BSV', 'DOGE', 'BAT',
  'USDC', 'USDT', 'USDC.e', 'EURC'
]

export const getTickerPriority = (ticker: string): number => {
  const index = TICKER_PRIORITY.indexOf(ticker)
  return index === -1 ? TICKER_PRIORITY.length : index
}

export interface PairTier {
  label: string
  accent: string
}

const normalizePairKey = (a: string, b: string): string => [a, b].sort().join('-')
const TIER_1_KEYS = new Set(TIER_1_PAIRS.map(p => normalizePairKey(...p.split('-') as [string, string])))
const TIER_2_KEYS = new Set(TIER_2_PAIRS.map(p => normalizePairKey(...p.split('-') as [string, string])))

export const classifyPairTier = (primaryTicker: string, secondaryTicker: string): PairTier => {
  const key = normalizePairKey(primaryTicker, secondaryTicker)
  if (TIER_1_KEYS.has(key)) return { label: 'Tier 1', accent: RADOM_COLORS.NICE_GREEN }
  if (TIER_2_KEYS.has(key)) return { label: 'Tier 2', accent: RADOM_COLORS.NEW_BLUE }
  return { label: 'Multi-leg', accent: '#8b5cf6' }
}

export const getInstId =
(fromAsset: SwapAssetRef,
  toAsset: SwapAssetRef): string => {
  const fromMethod = getMethod(fromAsset.network, fromAsset?.token ?? undefined)
  const toMethod = getMethod(toAsset.network, toAsset?.token ?? undefined)
  const [base, quote] = [fromMethod.ticker, toMethod.ticker]
    .sort((a, b) => getTickerPriority(a) - getTickerPriority(b))
  return `${base}-${quote}`
}

export interface SwapAssetRef {
  network: string
  token?: string | null
}

export interface SwapMarket {
  base: string
  quote: string
  baseLogo: string
  quoteLogo: string
  baseAsset: SwapAssetRef
  quoteAsset: SwapAssetRef
  price: number | null
  priceUnit: string
  change24h: number | null
  volume: string | null
}

const swapAssetKey = (a: SwapAssetRef): string => `${a.network}:${a.token ?? ''}`

const isAllowedStableLeg = (asset: SwapAssetRef, ticker: string): boolean =>
  (ticker !== 'USDC' && ticker !== 'USDT') || asset.network === 'Ethereum'

export const formatCompactNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '0'
  return Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(value)
}

const fetchSwapMarketTickers = async (): Promise<Map<string, ISwapMarketTicker>> => {
  try {
    const { tickers } = await Radom.getSwapMarketTickers()
    return new Map(tickers.map(t => [t.instId, t]))
  } catch (err) {
    console.error('Failed to load swap market tickers', err)
    return new Map()
  }
}

const ethereumLegCount = (m: SwapMarket): number =>
  (m.baseAsset.network === 'Ethereum' ? 1 : 0) + (m.quoteAsset.network === 'Ethereum' ? 1 : 0)

const dedupeUnifiedMarkets = (markets: SwapMarket[]): SwapMarket[] => {
  const byPair = new Map<string, SwapMarket>()
  for (const market of markets) {
    const key = `${market.base}/${market.quote}`
    const current = byPair.get(key)
    if (!current || ethereumLegCount(market) > ethereumLegCount(current)) {
      byPair.set(key, market)
    }
  }
  return [...byPair.values()]
}

export const fetchSwapMarkets = async (unifiedTreasury: boolean): Promise<SwapMarket[]> => {
  const { assets } = await Radom.getSwapSupportedAssets()

  const { results } = await Radom.getSwapSupportedTargetsBatch(assets)

  const pairsByKey = new Map<string, [SwapAssetRef, SwapAssetRef]>()
  for (const { from: asset, targets } of results) {
    for (const target of targets) {
      const canonicalKey = [swapAssetKey(asset), swapAssetKey(target)].sort().join('|')
      if (!pairsByKey.has(canonicalKey)) {
        pairsByKey.set(canonicalKey, [asset, target])
      }
    }
  }

  const tickersByInstId = await fetchSwapMarketTickers()

  const markets = Array.from(pairsByKey.values())
    .map(([a, b]): SwapMarket => {
      const methodA = getMethod(a.network, a.token ?? undefined)
      const methodB = getMethod(b.network, b.token ?? undefined)
      const [baseAsset, quoteAsset, baseMethod, quoteMethod] =
        getTickerPriority(methodA.ticker) <= getTickerPriority(methodB.ticker)
          ? [a, b, methodA, methodB]
          : [b, a, methodB, methodA]

      const ticker = tickersByInstId.get(`${baseMethod.ticker}-${quoteMethod.ticker}`)
      let last = ticker?.last ?? null
      let open24h = ticker?.open24h ?? null

      if (last === null || open24h === null) {
        const basePrice = (baseMethod.ticker === 'USDT' || baseMethod.ticker === 'USDC')
          ? { last: 1, open24h: 1 }
          : (tickersByInstId.get(`${baseMethod.ticker}-USDT`) || tickersByInstId.get(`${baseMethod.ticker}-USDC`))

        const quotePrice = (quoteMethod.ticker === 'USDT' || quoteMethod.ticker === 'USDC')
          ? { last: 1, open24h: 1 }
          : (tickersByInstId.get(`${quoteMethod.ticker}-USDT`) || tickersByInstId.get(`${quoteMethod.ticker}-USDC`))

        if (basePrice?.last && quotePrice?.last && quotePrice.last > 0) {
          last = basePrice.last / quotePrice.last
        }
        if (basePrice?.open24h && quotePrice?.open24h && quotePrice.open24h > 0) {
          open24h = basePrice.open24h / quotePrice.open24h
        }
      }

      const change24h = last !== null && open24h ? ((last - open24h) / open24h) * 100 : null
      const volume = ticker?.volCcy24h != null ? formatCompactNumber(ticker.volCcy24h) : null

      return {
        base: baseMethod.ticker,
        quote: quoteMethod.ticker,
        baseLogo: baseMethod.logo,
        quoteLogo: quoteMethod.logo,
        baseAsset,
        quoteAsset,
        price: last,
        priceUnit: quoteMethod.ticker,
        change24h,
        volume
      }
    })
    .filter(m =>
      m.base !== 'Unknown' && m.quote !== 'Unknown' && m.price !== null && m.base !== m.quote &&
      isAllowedStableLeg(m.baseAsset, m.base) && isAllowedStableLeg(m.quoteAsset, m.quote)
    )
    .sort((x, y) =>
      (getTickerPriority(x.base) - getTickerPriority(y.base)) ||
      (getTickerPriority(x.quote) - getTickerPriority(y.quote))
    )

  return unifiedTreasury ? dedupeUnifiedMarkets(markets) : markets
}

export const DEFAULT_FROM_PAIR: SwapAssetRef = { network: 'Ethereum', token: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48' }
export const DEFAULT_TO_PAIR: SwapAssetRef = { network: 'Bitcoin' }